import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Scale, User, Phone, Mail, MapPin, FileText, Briefcase, Calendar,
  CheckCircle, Clock, AlertCircle, Copy, Paperclip, Download, RefreshCw, PhoneCall,
  Send, StickyNote, X, Sparkles, Lock, Target
} from "lucide-react";
import LegalAnalysisDisplay from "@/components/admin/LegalAnalysisDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeaders() { return { headers: { Authorization: `Bearer ${localStorage.getItem("attorneyToken")}` } }; }

const OUTCOMES = [
  { value: "pending", label: "New / Pending" },
  { value: "contacted", label: "Contacted Client" },
  { value: "won", label: "Won — Client Retained" },
  { value: "lost", label: "Lost — Did Not Retain" },
  { value: "no_response", label: "No Response" },
];

const outcomeColors = {
  pending: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  contacted: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  won: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  lost: "bg-red-500/10 text-red-400 border-red-500/20",
  no_response: "bg-gray-500/10 text-gray-400 border-gray-500/20",
};

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
const getFileIcon = (ct) => {
  if (ct?.includes("pdf")) return "📄";
  if (ct?.includes("image")) return "🖼️";
  if (ct?.includes("word") || ct?.includes("doc")) return "📝";
  if (ct?.includes("sheet") || ct?.includes("excel")) return "📊";
  return "📎";
};

function Field({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-primary/60" />}
        <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-sm text-foreground leading-relaxed break-words">{value}</p>
    </div>
  );
}

const scoreColor = (s) => s == null ? "text-muted-foreground" : s >= 70 ? "text-emerald-400" : s >= 40 ? "text-amber-400" : "text-red-400";

export default function AttorneyLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("info");
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  // Lead Score workspace
  const [boosters, setBoosters] = useState(null);
  const [enrich, setEnrich] = useState({});
  const [enrichNote, setEnrichNote] = useState("");
  const [savingEnrich, setSavingEnrich] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState(null);
  const [genStrength, setGenStrength] = useState(false);
  // Notes
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/attorney/leads/${leadId}`, authHeaders());
        setLead(res.data);
      } catch (err) {
        if (err.response?.status === 401) navigate("/attorney/login");
        else setError("Failed to load lead.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [leadId, navigate]);

  useEffect(() => {
    if (tab === "documents") fetchDocuments();
    if (tab === "scoring") fetchBoosters();
  }, [tab]);

  const fetchBoosters = async () => {
    try {
      const res = await axios.get(`${API}/attorney/leads/${leadId}/score-boosters`, authHeaders());
      setBoosters(res.data);
    } catch { /* non-fatal */ }
  };

  const saveEnrichment = async () => {
    const fields = Object.fromEntries(Object.entries(enrich).filter(([, v]) => v && v.trim()));
    if (Object.keys(fields).length === 0 && !enrichNote.trim()) {
      setEnrichMsg({ type: "warn", text: "Enter some info or a note before saving." });
      return;
    }
    setSavingEnrich(true); setEnrichMsg(null);
    try {
      const res = await axios.post(`${API}/attorney/leads/${leadId}/enrich`,
        { fields, note: enrichNote.trim() || undefined }, authHeaders());
      setLead(res.data.lead);
      setEnrich({}); setEnrichNote("");
      fetchBoosters();
      setEnrichMsg({ type: "ok", text: `Saved & re-scored — new overall ${res.data.case_score?.overall_score ?? "—"}/100.` });
      setTimeout(() => setEnrichMsg(null), 6000);
    } catch {
      setEnrichMsg({ type: "err", text: "Failed to save. Try again." });
    } finally { setSavingEnrich(false); }
  };

  const generateStrengthen = async () => {
    setGenStrength(true);
    try {
      const res = await axios.post(`${API}/attorney/leads/${leadId}/strengthen`, {}, authHeaders());
      setLead((p) => ({ ...p, strengthen_analysis: res.data.strengthen_analysis }));
    } catch { setError("Failed to generate suggestions."); }
    finally { setGenStrength(false); }
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setAddingNote(true);
    try {
      const res = await axios.post(`${API}/attorney/leads/${leadId}/notes`, { text }, authHeaders());
      const note = { ...res.data.note, text };
      setLead((p) => ({ ...p, notes: [...(p.notes || []), note] }));
      setNoteText("");
    } catch { setError("Failed to add note."); }
    finally { setAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/attorney/leads/${leadId}/notes/${noteId}`, authHeaders());
      setLead((p) => ({ ...p, notes: (p.notes || []).filter((n) => n.note_id !== noteId) }));
    } catch { setError("Failed to delete note."); }
  };

  const fetchDocuments = async () => {
    setDocsLoading(true);
    try {
      const res = await axios.get(`${API}/attorney/leads/${leadId}/documents`, authHeaders());
      setDocuments(res.data.documents || []);
    } catch { /* non-fatal */ }
    finally { setDocsLoading(false); }
  };

  const downloadDocument = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/attorney/documents/${docId}/download`, { ...authHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setError("Failed to download document."); }
  };

  // This firm's OWN call tracking — only called/reachable. 'confirmed' is admin-owned (read-only here).
  const toggleCall = async (flag) => {
    if (flag === "confirmed") return; // guard: attorneys cannot change admin's confirmation
    const cur = lead.my_call || {};
    const nextVal = !cur[flag];
    setLead((p) => ({ ...p, my_call: { ...cur, [flag]: nextVal } }));
    try { await axios.put(`${API}/attorney/leads/${leadId}/call-status`, { [flag]: nextVal }, authHeaders()); }
    catch { setLead((p) => ({ ...p, my_call: cur })); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <RefreshCw className="w-6 h-6 text-primary animate-spin" />
    </div>
  );

  if (error && !lead) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-sm text-destructive flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>
    </div>
  );

  const docCount = documents.length;

  const TABS = [
    { id: "info", label: "Lead Info" },
    { id: "scoring", label: "Lead Score" },
    { id: "notes", label: `Notes${(lead?.notes?.length || 0) > 0 ? ` (${lead.notes.length})` : ""}` },
    { id: "legal", label: "Legal Analysis" },
    { id: "transcript", label: `Chat (${lead?.conversation?.length || 0})` },
    { id: "documents", label: "Documents" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/attorney/dashboard")} className="p-1.5 rounded-lg hover:bg-muted transition text-muted-foreground">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-7 h-7 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center">
            <Scale className="w-3.5 h-3.5 text-primary" />
          </div>
          <span className="font-semibold text-sm truncate">{lead?.name || "Lead Detail"}</span>
          <span className={`ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-full border ${outcomeColors[lead?.attorney_lead_status || "pending"]}`}>
            {OUTCOMES.find(o => o.value === (lead?.attorney_lead_status || "pending"))?.label || "New"}
          </span>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left — tabbed content (mirrors admin) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tab bar */}
          <div className="flex gap-1 p-1 rounded-xl bg-secondary/30 border border-border/40 overflow-x-auto">
            {TABS.map(({ id, label }) => (
              <button key={id} onClick={() => setTab(id)}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${tab === id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Lead Info */}
          {tab === "info" && (
            <div className="space-y-3 animate-fade-in">
              {/* Case Score banner */}
              {lead?.case_score && (
                <div className="p-4 rounded-xl border border-border/30 bg-card/20">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">AI Case Score</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: "Overall", value: lead.case_score.overall_score },
                      { label: "Win Prob.", value: lead.case_score.win_probability != null ? `${lead.case_score.win_probability}%` : "—", raw: lead.case_score.win_probability },
                      { label: "Clarity", value: lead.case_score.clarity_score },
                      { label: "Urgency", value: lead.case_score.urgency_score },
                    ].map(({ label, value, raw }) => {
                      const num = raw ?? (typeof value === "number" ? value : null);
                      return (
                        <div key={label} className="text-center p-2 rounded-lg bg-secondary/20">
                          <div className={`text-lg font-bold ${scoreColor(num)}`}>{value ?? "—"}</div>
                          <div className="text-xs text-muted-foreground">{label}</div>
                        </div>
                      );
                    })}
                  </div>
                  {lead.case_score.rationale && <p className="text-xs text-muted-foreground italic leading-relaxed mt-3">{lead.case_score.rationale}</p>}
                </div>
              )}

              {/* Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field icon={User} label="Full Name" value={lead?.name} />
                <Field icon={Phone} label="Phone" value={lead?.phone} />
                <Field icon={Mail} label="Email" value={lead?.email} />
                <Field icon={MapPin} label="Location" value={lead?.location} />
                <Field icon={Clock} label="Contact Preference" value={lead?.contact_preference} />
                <Field icon={Calendar} label="Availability" value={lead?.availability} />
              </div>

              {/* Case details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field icon={Briefcase} label="Case Category" value={lead?.case_category} />
                <Field icon={Briefcase} label="Case Type" value={lead?.case_type} />
                <Field icon={User} label="Parties Involved" value={lead?.parties_involved} />
                <Field icon={Calendar} label="Incident Date" value={lead?.incident_date} />
                <Field icon={Clock} label="Current Status" value={lead?.current_status} />
                <Field icon={FileText} label="Documents Available" value={lead?.documents} />
                <Field icon={FileText} label="Prior Consultation" value={lead?.prior_consultation} />
                <Field icon={CheckCircle} label="Fee Type" value={lead?.fee_type} />
                <Field icon={CheckCircle} label="Budget" value={lead?.fee_budget} />
              </div>

              {/* Desired Outcome — what the client wants */}
              <div className="p-3 sm:p-4 rounded-xl border border-primary/20 bg-primary/[0.04]">
                <div className="flex items-center gap-2 mb-1.5">
                  <Target className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs text-primary uppercase tracking-wider font-semibold">Desired Outcome</span>
                </div>
                <p className="text-sm text-foreground leading-relaxed">
                  {lead?.desired_outcome || lead?.case_analysis?.desired_outcome
                    || <span className="text-muted-foreground italic">Not captured during intake.</span>}
                </p>
              </div>

              {lead?.fact_summary && <Field icon={FileText} label="Fact Summary" value={lead.fact_summary} />}
              {lead?.detailed_story && <Field icon={FileText} label="Detailed Story" value={lead.detailed_story} />}
              {lead?.evidence_summary && <Field icon={FileText} label="Evidence" value={lead.evidence_summary} />}
              {lead?.claim_strengths && <Field icon={FileText} label="Claim Strengths" value={lead.claim_strengths} />}

              {/* 7-section Case Analysis */}
              {lead?.case_analysis && (() => {
                const a = lead.case_analysis;
                const List = ({ title, items }) => (Array.isArray(items) && items.length > 0) ? (
                  <div className="mb-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/80 mb-1">{title}</p>
                    <ul className="space-y-1">{items.map((it, i) => <li key={i} className="flex items-start gap-2 text-sm"><span className="text-primary mt-0.5">•</span><span>{it}</span></li>)}</ul>
                  </div>
                ) : null;
                return (
                  <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <FileText className="w-4 h-4 text-primary" />
                      <h2 className="text-sm font-semibold">Case Analysis</h2>
                    </div>
                    {a.case_type && <p className="text-sm font-semibold mb-1">{a.case_type}</p>}
                    {Array.isArray(a.sub_types) && a.sub_types.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">{a.sub_types.map((s, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary">{s}</span>)}</div>
                    )}
                    {a.incident_summary && <p className="text-sm text-foreground/90 mb-3 leading-relaxed">{a.incident_summary}</p>}
                    <List title="Background" items={a.background} />
                    <List title="Key Facts" items={a.key_facts} />
                    <List title="Potential Claims" items={a.potential_claims} />
                    <List title="Evidence Available" items={a.evidence} />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Lead Score workspace — identical to admin */}
          {tab === "scoring" && (() => {
            const cs = lead.case_score || {};
            const la = lead.legal_analysis || {};
            const overall = cs.overall_score;
            const ring = overall == null ? "text-muted-foreground border-border"
              : overall >= 70 ? "text-emerald-400 border-emerald-500/40"
              : overall >= 40 ? "text-amber-400 border-amber-500/40"
              : "text-red-400 border-red-500/40";
            const subColor = (n) => n == null ? "text-muted-foreground" : n >= 70 ? "text-emerald-400" : n >= 40 ? "text-amber-400" : "text-red-400";
            const positives = [];
            const negatives = [];
            if (cs.win_probability >= 60) positives.push(`Strong win probability (${cs.win_probability}%)`);
            else if (cs.win_probability != null && cs.win_probability < 40) negatives.push(`Low win probability (${cs.win_probability}%)`);
            if (cs.clarity_score >= 70) positives.push("Case facts are clear and complete");
            else if (cs.clarity_score != null && cs.clarity_score < 50) negatives.push("Case details are incomplete / unclear");
            if (cs.urgency_score >= 70) positives.push("Time-sensitive — high urgency");
            (la.case_strengths || []).forEach(s => positives.push(s));
            (la.red_flags || []).forEach(s => negatives.push(s));
            if (la.sol_status === "At Risk" || la.sol_status === "Potentially Expired") negatives.push(`Statute of limitations: ${la.sol_status}`);

            const enrichFields = [
              { key: "employment_status", label: "Employment Status", placeholder: "e.g. Full-time, unemployed, self-employed" },
              { key: "income", label: "Income", placeholder: "e.g. $65k/yr, hourly, unable to pay" },
              { key: "incident_date", label: "Incident Date", placeholder: "When did it happen?" },
              { key: "current_status", label: "Current Status", placeholder: "Where does the matter stand now?" },
              { key: "parties_involved", label: "Parties Involved", placeholder: "Other people / companies involved" },
              { key: "documents", label: "Documents / Evidence", placeholder: "What do they have on hand?" },
              { key: "phone", label: "Phone", placeholder: "Best contact number" },
              { key: "email", label: "Email", placeholder: "Email address" },
              { key: "location", label: "Location", placeholder: "City / state / jurisdiction" },
            ];

            return (
              <div className="space-y-4 animate-fade-in">
                <div className="p-5 rounded-2xl border border-border/30 bg-card/20 flex flex-col sm:flex-row items-center gap-5">
                  <div className={`w-24 h-24 rounded-full border-4 ${ring} flex flex-col items-center justify-center flex-shrink-0`}>
                    <span className="text-3xl font-bold leading-none">{overall ?? "—"}</span>
                    <span className="text-[10px] text-muted-foreground mt-0.5">/ 100</span>
                  </div>
                  <div className="flex-1 w-full">
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {[
                        { label: "Win Prob.", v: cs.win_probability != null ? `${cs.win_probability}%` : "—", n: cs.win_probability },
                        { label: "Clarity", v: cs.clarity_score ?? "—", n: cs.clarity_score },
                        { label: "Urgency", v: cs.urgency_score ?? "—", n: cs.urgency_score },
                      ].map(({ label, v, n }) => (
                        <div key={label} className="text-center p-2 rounded-lg bg-secondary/20">
                          <div className={`text-lg font-bold ${subColor(n)}`}>{v}</div>
                          <div className="text-xs text-muted-foreground">{label}</div>
                        </div>
                      ))}
                    </div>
                    {cs.rationale && <p className="text-xs text-muted-foreground italic leading-relaxed">{cs.rationale}</p>}
                    {!lead.case_score && <p className="text-xs text-muted-foreground">No score yet — add info below and save to generate one.</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                    <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5" /> Positive Factors
                    </h4>
                    {positives.length ? (
                      <ul className="space-y-1.5">{positives.map((p, i) => <li key={i} className="text-xs text-foreground/90 flex gap-1.5"><span className="text-emerald-400">+</span>{p}</li>)}</ul>
                    ) : <p className="text-xs text-muted-foreground">None identified yet.</p>}
                  </div>
                  <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5">
                    <h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <X className="w-3.5 h-3.5" /> Negative Factors
                    </h4>
                    {negatives.length ? (
                      <ul className="space-y-1.5">{negatives.map((p, i) => <li key={i} className="text-xs text-foreground/90 flex gap-1.5"><span className="text-red-400">–</span>{p}</li>)}</ul>
                    ) : <p className="text-xs text-muted-foreground">None identified.</p>}
                  </div>
                </div>

                {/* Strengthen the case — AI questions to ask to raise win rate & case value */}
                {(() => {
                  const st = lead.strengthen_analysis;
                  return (
                    <div className="p-4 rounded-xl border border-violet-500/25 bg-violet-500/[0.06]">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h4 className="text-xs font-semibold text-violet-300 uppercase tracking-wider flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5" /> Strengthen the Case · Win-Rate Boosters
                        </h4>
                        <button onClick={generateStrengthen} disabled={genStrength}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500/15 text-violet-200 border border-violet-500/30 text-xs font-semibold hover:bg-violet-500/25 transition disabled:opacity-50">
                          <RefreshCw className={`w-3.5 h-3.5 ${genStrength ? "animate-spin" : ""}`} />
                          {genStrength ? "Analyzing…" : st ? "Regenerate" : "Generate suggestions"}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">AI-suggested questions to ask and info to gather that make this case bigger, more solid, and more likely to win.</p>
                      {!st ? (
                        <p className="text-xs text-muted-foreground italic">No suggestions yet — click <strong>Generate suggestions</strong> for case-specific guidance.</p>
                      ) : (
                        <div className="space-y-3">
                          {st.summary && <p className="text-sm text-foreground/90 leading-relaxed bg-violet-500/10 rounded-lg px-3 py-2 border border-violet-500/20">💡 {st.summary}</p>}
                          {Array.isArray(st.questions_to_ask) && st.questions_to_ask.length > 0 && (
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-300/80 mb-1">Questions to Ask the Client</p>
                              <ul className="space-y-1">{st.questions_to_ask.map((q, i) => <li key={i} className="text-xs text-foreground/85 flex gap-1.5"><span className="text-violet-400">?</span>{q}</li>)}</ul>
                            </div>
                          )}
                          {Array.isArray(st.win_rate_levers) && st.win_rate_levers.length > 0 && (
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-400/80 mb-1">What Raises the Win Rate</p>
                              <ul className="space-y-1">{st.win_rate_levers.map((q, i) => <li key={i} className="text-xs text-foreground/85 flex gap-1.5"><span className="text-emerald-400">↑</span>{q}</li>)}</ul>
                            </div>
                          )}
                          {Array.isArray(st.value_drivers) && st.value_drivers.length > 0 && (
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-400/80 mb-1">What Makes the Case Bigger</p>
                              <ul className="space-y-1">{st.value_drivers.map((q, i) => <li key={i} className="text-xs text-foreground/85 flex gap-1.5"><span className="text-amber-400">$</span>{q}</li>)}</ul>
                            </div>
                          )}
                          {st.generated_at && <p className="text-[10px] text-muted-foreground/60">Generated {new Date(st.generated_at).toLocaleString()}</p>}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="p-4 rounded-xl border border-primary/20 bg-primary/5">
                  <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" /> Improve This Lead's Score
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Missing information to collect</p>
                      {boosters?.missing_info?.length ? (
                        <ul className="space-y-1">{boosters.missing_info.map((m, i) => <li key={i} className="text-xs text-foreground/80 flex gap-1.5"><span className="text-amber-400">•</span>{m.label}</li>)}</ul>
                      ) : <p className="text-xs text-muted-foreground">All key fields collected. 🎉</p>}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Recommended actions</p>
                      {boosters?.recommended_actions?.length ? (
                        <ul className="space-y-1">{boosters.recommended_actions.map((a, i) => <li key={i} className="text-xs text-foreground/80 flex gap-1.5"><span className="text-primary">→</span>{a}</li>)}</ul>
                      ) : <p className="text-xs text-muted-foreground">No tailored steps yet.</p>}
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-border/30 bg-card/20">
                  <h4 className="text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-primary/60" /> Collect Info While On The Call
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">Fill anything you learn. Saving merges it into the lead and automatically re-scores.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {enrichFields.map(({ key, label, placeholder }) => (
                      <div key={key}>
                        <label className="text-xs text-muted-foreground mb-1 block">{label}{lead[key] ? <span className="text-emerald-400/70 ml-1">(on file)</span> : ""}</label>
                        <input
                          value={enrich[key] ?? ""}
                          onChange={(e) => setEnrich((prev) => ({ ...prev, [key]: e.target.value }))}
                          placeholder={lead[key] ? `Current: ${String(lead[key]).slice(0, 40)}` : placeholder}
                          className="w-full px-3 py-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <label className="text-xs text-muted-foreground mb-1 block">Case Details / Notes</label>
                    <textarea
                      value={enrichNote}
                      onChange={(e) => setEnrichNote(e.target.value)}
                      rows={3}
                      placeholder="Anything else worth recording about the case or the call..."
                      className="w-full px-3 py-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                    />
                  </div>
                  {enrichMsg && (
                    <p className={`text-xs mt-2 ${enrichMsg.type === "ok" ? "text-emerald-400" : enrichMsg.type === "err" ? "text-red-400" : "text-amber-400"}`}>{enrichMsg.text}</p>
                  )}
                  <div className="flex justify-end mt-3">
                    <button onClick={saveEnrichment} disabled={savingEnrich}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-50">
                      <RefreshCw className={`w-3.5 h-3.5 ${savingEnrich ? "animate-spin" : ""}`} />
                      {savingEnrich ? "Saving & re-scoring..." : "Save & Re-score"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Notes — shared thread (mutual database with admin) */}
          {tab === "notes" && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h2 className="text-sm font-semibold mb-1">Call Notes</h2>
                <p className="text-xs text-muted-foreground">Shared with the admin team. Add notes as you call or follow up with this client.</p>
              </div>
              <div className="rounded-xl border border-border/30 bg-card/20 p-4 space-y-3">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAddNote(); }}
                  placeholder="Left a voicemail at 2:30 PM... / Spoke with client, scheduling consult Friday..."
                  rows={3}
                  className="w-full bg-secondary/30 border border-border/30 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Cmd/Ctrl + Enter to save</span>
                  <button onClick={handleAddNote} disabled={addingNote || !noteText.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-all disabled:opacity-50">
                    <Send className="w-3 h-3" />{addingNote ? "Saving..." : "Add Note"}
                  </button>
                </div>
              </div>
              {!lead.notes?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <StickyNote className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No notes yet</p>
                  <p className="text-xs mt-1">Add your first note above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {[...(lead.notes || [])].reverse().map((note) => (
                    <div key={note.note_id} className="p-4 rounded-xl border border-border/30 bg-card/20 group">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm leading-relaxed flex-1 whitespace-pre-wrap">{note.text}</p>
                        <button onClick={() => handleDeleteNote(note.note_id)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all flex-shrink-0">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">{note.author}</span>
                        <span className="text-muted-foreground/30">·</span>
                        <span className="text-xs text-muted-foreground">{note.created_at ? new Date(note.created_at).toLocaleString() : ""}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Legal Analysis — identical to admin (shared component, no admin reanalyze action) */}
          {tab === "legal" && (
            <div className="animate-fade-in">
              <LegalAnalysisDisplay analysis={lead?.legal_analysis} />
            </div>
          )}

          {/* Transcript */}
          {tab === "transcript" && (
            <div className="space-y-3 animate-fade-in">
              {lead?.conversation?.length > 0 && (
                <div className="flex justify-end mb-1">
                  <button
                    onClick={() => {
                      const text = lead.conversation.map(m => `${m.role === "assistant" ? "Sasha" : "Client"}: ${m.content}`).join("\n\n");
                      navigator.clipboard.writeText(text).then(() => { setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); });
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-secondary/60 hover:bg-secondary border border-border/30 transition-all">
                    <Copy className="w-3.5 h-3.5" />{copySuccess ? "Copied!" : "Copy Full Convo"}
                  </button>
                </div>
              )}
              {(!lead?.conversation || lead.conversation.length === 0) ? (
                <div className="text-center py-10 text-muted-foreground text-sm">No transcript available</div>
              ) : (
                lead.conversation.map((msg, i) => (
                  <div key={i} className={`flex gap-2 sm:gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 ${msg.role === "assistant" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
                      {msg.role === "assistant" ? "S" : "C"}
                    </div>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${msg.role === "user" ? "bg-primary/10 border border-primary/20" : "bg-card border border-border/40"}`}>
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Documents */}
          {tab === "documents" && (
            <div className="space-y-3 animate-fade-in">
              {docsLoading ? (
                <div className="text-center py-10 text-muted-foreground"><RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />Loading documents...</div>
              ) : documents.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground border border-border/30 rounded-xl">
                  <Paperclip className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No documents uploaded</p>
                  <p className="text-xs mt-1">Documents uploaded during intake will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.doc_id} className="flex items-center gap-3 p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20 hover:bg-card/30 transition-all">
                      <div className="text-2xl flex-shrink-0">{getFileIcon(doc.content_type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                        <p className="text-xs text-muted-foreground">{formatBytes(doc.size)} · {new Date(doc.created_at).toLocaleDateString()}</p>
                      </div>
                      <button onClick={() => downloadDocument(doc.doc_id, doc.original_filename)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all" title="Download">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right — call status + meta (outcome is managed by admin) */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5 sticky top-20">
            <div className="flex items-center gap-2 mb-1">
              <PhoneCall className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold">Call &amp; Contact</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Your firm's own tracking — private to you.</p>

            {/* Call status toggles — Called/Reachable are this firm's own; Confirmed is admin-controlled (read-only) */}
            <div className="space-y-2">
              {[
                { flag: "called", label: "Called", on: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: Phone },
                { flag: "reachable", label: "Reachable", on: "bg-violet-500/10 text-violet-300 border-violet-500/20", icon: PhoneCall },
              ].map(({ flag, label, on, icon: Icon }) => {
                const active = !!lead?.my_call?.[flag];
                return (
                  <button key={flag} onClick={() => toggleCall(flag)}
                    className={`w-full flex items-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold border transition ${active ? on : "bg-gray-500/10 text-gray-400 border-gray-500/20 hover:bg-gray-500/20"}`}>
                    <Icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{label}</span>
                    {active && <CheckCircle className="w-3.5 h-3.5" />}
                  </button>
                );
              })}

              {/* Contact Info Confirmed — set by the admin, shown to every firm, not editable here */}
              {(() => {
                const confirmed = !!lead?.my_call?.confirmed;
                return (
                  <div title="Confirmed by the admin team — shared across all assigned firms"
                    className={`w-full flex items-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold border cursor-default ${confirmed ? "bg-sky-500/10 text-sky-400 border-sky-500/20" : "bg-gray-500/10 text-gray-400 border-gray-500/20"}`}>
                    <CheckCircle className="w-4 h-4" />
                    <span className="flex-1 text-left">Contact Info Confirmed</span>
                    {confirmed
                      ? <CheckCircle className="w-3.5 h-3.5" />
                      : <Lock className="w-3 h-3 opacity-60" />}
                  </div>
                );
              })()}
              <p className="text-[10px] text-muted-foreground/70 px-1">Contact confirmation is set by the admin team.</p>
            </div>
            {!lead?.my_call?.called && <p className="text-[11px] text-muted-foreground/70 mt-2">Status: Not Called</p>}

            <p className="text-xs text-muted-foreground mt-3">
              Add call notes in the <strong className="text-foreground/80">Notes</strong> tab. Case outcome is tracked by the admin team.
            </p>

            {error && <p className="text-xs text-destructive mt-3">{error}</p>}

            {lead?.assignment_date && (
              <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border/40">
                Assigned {new Date(lead.assignment_date).toLocaleDateString()} via {lead.assignment_method || "admin"}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
